import { FaqItem } from "../models/home/faq-item";
import { ResourceArticleSlug } from "./resource-article-slugs";

export interface ResourceSection {
  readonly heading: string;
  readonly body: readonly string[];
  readonly bullets?: readonly string[];
}

export interface ResourceArticleCta {
  readonly title: string;
  readonly body: string;
  readonly label: string;
  readonly url: string;
}

export interface ResourceArticle {
  readonly slug: ResourceArticleSlug;
  readonly category: string;
  /** Page heading. Distinct from metaTitle, which is trimmed for the SERP. */
  readonly h1: string;
  readonly metaTitle: string;
  readonly metaDescription: string;
  /** Card summary on the hub, and the article lede. */
  readonly excerpt: string;
  readonly lede: string;
  readonly readMinutes: number;
  /** ISO date. Feeds dateModified in the Article schema. */
  readonly updated: string;
  readonly takeaways: readonly string[];
  readonly sections: readonly ResourceSection[];
  readonly cta: ResourceArticleCta;
  /** Not readonly: the shared <app-faq> component takes a mutable FaqItem[] input. */
  readonly faqs: FaqItem[];
}

export const ResourceArticles: readonly ResourceArticle[] = [
  {
    slug: "pre-foreclosure-timeline",
    category: "Pre-Foreclosure",
    h1: "The Pre-Foreclosure Timeline, From First Notice to Auction",
    metaTitle: "Pre-Foreclosure Timeline: Notice of Default to Auction",
    metaDescription:
      "How a pre-foreclosure moves from the first recorded notice to the auction date, which stage is worth contacting, and what the public record shows at each step.",
    excerpt:
      "Every pre-foreclosure runs on a clock that starts the day a notice hits the public record. Knowing where a filing sits on that clock decides whether your call is early enough to matter.",
    lede: "A pre-foreclosure is not a single event. It is a sequence of recorded filings with weeks or months between them, and the owner's willingness to talk changes completely depending on which stage they are in.",
    readMinutes: 6,
    updated: "2026-09-14",
    takeaways: [
      "The clock starts at the first recorded notice, not at the missed payment.",
      "Timelines vary by state and by whether the state uses a judicial or non-judicial process.",
      "The earliest stage is where the owner still has options, which is where a conversation is welcome.",
      "By the time an auction date is published, most of the owner's choices have closed."
    ],
    sections: [
      {
        heading: "Why the filing date matters more than the list",
        body: [
          "Two pre-foreclosure records can look identical in a spreadsheet and be completely different opportunities. One was filed last week. The other was filed nine months ago and already has a sale date. The first owner is still weighing options. The second has probably been called by every investor in the county and has either solved the problem or given up.",
          "This is why filing date is the single most useful column in a pre-foreclosure list, and why a list that was assembled once and resold for months is close to worthless. LeadCruncher refreshes pre-foreclosure filings daily, so the date on the record is the date the county recorded it."
        ]
      },
      {
        heading: "Stage one: the first recorded notice",
        body: [
          "The process becomes public when the lender records a notice. In non-judicial states this is typically a notice of default. In judicial states the lender files suit and a lis pendens is recorded to put the world on notice that the property is subject to litigation.",
          "Either way, this is the moment the situation stops being private. It is also the moment the owner is most likely to still have room to move: reinstate the loan, sell with equity intact, negotiate with the lender, or refinance."
        ],
        bullets: [
          "The owner usually has the most options available.",
          "Competition is lowest, because slower data sources have not published the filing yet.",
          "Equity, if there is any, is still the owner's to keep."
        ]
      },
      {
        heading: "Stage two: the waiting period",
        body: [
          "After the first notice there is a statutory window before the lender can move to sale. The length of that window is set by state law and differs substantially from state to state, so a filing that is two months old means something very different in one state than in another.",
          "This is the longest stretch of the timeline and the one where most contact happens. It is also where the owner's circumstances are most likely to change, which is an argument for following up rather than making one attempt and moving on."
        ]
      },
      {
        heading: "Stage three: the sale is scheduled",
        body: [
          "Eventually a sale date is published. At this point the deadline is concrete and the owner's remaining options are narrow and time-boxed. Some investors work exclusively here, because motivation is at its peak and the number of people still willing to engage is small.",
          "The trade-off is obvious: very little time to close anything, and a much higher chance the owner has already committed to a course of action."
        ]
      },
      {
        heading: "How to work the timeline with data",
        body: [
          "The practical workflow is to filter by county and filing date, pull the records that entered the process most recently, and contact them before the rest of the market sees them.",
          "Pre-foreclosure filings are included with an Assessors Data subscription at no extra cost, so the same account gives you the notice and the full parcel record behind it: ownership, equity position, tax detail and deed history. That context is what turns a name and an address into a conversation you can actually have."
        ],
        bullets: [
          "Filter to a county you can genuinely service.",
          "Sort by filing date and work the newest records first.",
          "Check the equity position before you decide what to offer.",
          "Set a follow-up cadence rather than treating one attempt as the whole effort."
        ]
      }
    ],
    cta: {
      title: "See the filings for your county",
      body: "1.4M+ notices of default and lis pendens filings, refreshed daily, included with every Assessors Data subscription at $60 per month.",
      label: "Explore Pre-Foreclosure Data",
      url: "/foreclosure-data"
    },
    faqs: [
      {
        question: "How often is pre-foreclosure data updated?",
        answer: "Daily. New filings are added as counties record them, so the filing date on a record reflects the county record rather than the date a list was assembled."
      },
      {
        question: "Do I need a separate subscription for pre-foreclosure data?",
        answer: "No. Pre-foreclosure data is included with an Assessors Data subscription at $60 per month, alongside the full parcel record for each property."
      },
      {
        question: "How long does a pre-foreclosure take to reach auction?",
        answer: "It depends on the state and on whether the state uses a judicial or non-judicial process. The statutory waiting period after the first recorded notice differs substantially between states, so compare filings within a state rather than across states."
      }
    ]
  },
  {
    slug: "how-to-find-probate-leads",
    category: "Probate",
    h1: "How to Find Probate Leads in Any County",
    metaTitle: "How to Find Probate Leads in Any County",
    metaDescription:
      "Where probate filings come from, who the estate representative is, why they are the person to contact, and how to work probate records county by county.",
    excerpt:
      "Probate property is sold by someone who never chose to own it, usually on a deadline set by a court. The hard part is not motivation. It is finding out who has the authority to sell.",
    lede: "Probate leads are unusual because the person with the legal authority to sell is rarely the person whose name is on the deed. Getting that distinction right is most of the work.",
    readMinutes: 6,
    updated: "2026-09-14",
    takeaways: [
      "The estate representative, not the deceased owner, is the person who can sell.",
      "Probate filings are county court records, which is why coverage is county by county.",
      "The attorney of record is often the faster and more appropriate first contact.",
      "Timing matters: too early is intrusive, too late means the property is already listed."
    ],
    sections: [
      {
        heading: "What a probate filing actually is",
        body: [
          "When someone dies owning property, the estate generally passes through a court process that confirms who may act on the estate's behalf. That process creates a public court record naming an estate representative, sometimes called an executor or personal representative depending on the state.",
          "That filing is the lead. It tells you a property is likely to change hands, and it tells you the one person who can lawfully agree to sell it."
        ]
      },
      {
        heading: "Why the representative is the only useful contact",
        body: [
          "Mailing the deceased owner's name to the property address is the most common mistake in probate marketing. The owner has died, and the property is often vacant, so the mail reaches nobody.",
          "LeadCruncher's probate records carry the representative's name and mailing address alongside the property detail, plus the attorney of record where one is on file: name, bar number, firm address and contact details. For many investors the attorney is the better first call, because it is a professional conversation rather than an unexpected approach to a grieving family."
        ],
        bullets: [
          "Estate representative name and mailing address.",
          "Attorney of record, including bar number and firm contact details.",
          "The property record itself, so you know what is actually being discussed."
        ]
      },
      {
        heading: "Working county by county",
        body: [
          "Probate records originate with county clerks, which is why probate coverage is described county by county rather than as a single national feed. LeadCruncher's probate data is compiled from county clerk records and updated weekly, every Tuesday.",
          "A weekly cadence is the honest answer here. Court records are not published continuously, and a source claiming minute-by-minute probate updates is describing something other than what the courts actually release."
        ]
      },
      {
        heading: "Getting the timing right",
        body: [
          "Probate is the one lead type where contacting too quickly can cost you the deal outright. A filing that is days old means a family that is still arranging a funeral.",
          "Most experienced operators wait until the estate has had time to organise itself, then lead with a letter rather than a call, and make it clear they understand the property is part of an estate. The filings are not going anywhere, and a slightly later approach that is welcome beats an immediate one that is not."
        ]
      },
      {
        heading: "Turning a filing into a conversation",
        body: [
          "Once you have the representative, you need a way to reach them. Skip tracing returns phone numbers and emails for an address at $0.25 per record, with no subscription required, and results come back instantly.",
          "From there the outreach tools are already in the account: mail merge letters for the estate representative, the CRM to keep every filing and note against the right record, and the triple line dialer, which is included free with every plan."
        ]
      }
    ],
    cta: {
      title: "Start with your own county",
      body: "600,000+ probate filings with the estate representative, attorney of record and full property detail, updated every Tuesday from county clerk records. $125 per month.",
      label: "Explore Probate Leads",
      url: "/probate-leads"
    },
    faqs: [
      {
        question: "How often are probate records updated?",
        answer: "Weekly, every Tuesday. Probate records come from county clerks, which release filings on their own schedule rather than continuously."
      },
      {
        question: "Does probate data include the attorney handling the estate?",
        answer: "Yes, where an attorney is on file. Records include the attorney name, bar number, firm address and contact details alongside the estate representative."
      },
      {
        question: "Should I contact the representative or the attorney first?",
        answer: "Many investors prefer the attorney, because it keeps the first approach professional. The representative is the person who can ultimately agree to a sale, so both contacts are provided."
      }
    ]
  },
  {
    slug: "skip-tracing-accuracy",
    category: "Skip Tracing",
    h1: "What Skip Tracing Accuracy Actually Means",
    metaTitle: "What Skip Tracing Accuracy Actually Means",
    metaDescription:
      "Accuracy, match rate and connect rate are three different numbers. Here is what each one measures, and what to check before you judge a skip tracing provider.",
    excerpt:
      "Providers quote accuracy, match rate and connect rate as if they were interchangeable. They measure different things, and confusing them is how people end up disappointed by a perfectly good result.",
    lede: "Skip tracing is sold on a single percentage, which is unhelpful, because at least three different percentages are in circulation and they do not mean the same thing.",
    readMinutes: 5,
    updated: "2026-09-14",
    takeaways: [
      "Accuracy asks whether a returned contact is correct. Match rate asks whether anything was returned at all.",
      "Connect rate is an outcome of your calling, not a property of the data.",
      "A provider that promises a fixed number of phone numbers per record is describing volume, not quality.",
      "Compliance is your responsibility, and your own do-not-call list is part of it."
    ],
    sections: [
      {
        heading: "Three numbers, three meanings",
        body: [
          "Accuracy is the share of returned contacts that genuinely belong to the person you asked about. Match rate is the share of submitted records that came back with anything at all. Connect rate is the share of your calls that reached a human being.",
          "These move independently. A provider can return something for almost every record and still be wrong half the time. Another can return fewer contacts that are almost always right. Only the second is useful, because wrong numbers cost you call time and goodwill."
        ]
      },
      {
        heading: "What LeadCruncher reports",
        body: [
          "LeadCruncher skip tracing runs at 98% accuracy, and around nine in ten records return contact information you can act on. Results are returned instantly at $0.25 per record, with no subscription required.",
          "We do not quote a guaranteed number of phone numbers per record, because that number is not ours to promise. What comes back depends on the person and on what the underlying records actually contain."
        ],
        bullets: [
          "98% accuracy on returned contacts.",
          "Roughly nine in ten records return usable contact information.",
          "Instant results, $0.25 per record, no subscription."
        ]
      },
      {
        heading: "Why a bad number is worse than no number",
        body: [
          "An unmatched record costs you nothing but the lookup. A wrong number costs you a dial, the time spent on the call, and occasionally an irritated stranger who has no connection to the property.",
          "At scale this matters more than the headline match rate. Fifty extra contacts that are wrong will waste more of your week than fifty missing ones would have."
        ]
      },
      {
        heading: "Compliance is still on you",
        body: [
          "Skip tracing returns contact details. It does not decide whether you may lawfully call them. Calling rules, consent requirements and do-not-call obligations vary and they apply to you as the caller.",
          "The dialer supports blocking against a do-not-call list that you upload and maintain yourself. That is a tool for honouring your own suppression list, and it is not a substitute for your own compliance process or for legal advice."
        ]
      },
      {
        heading: "How to judge a provider honestly",
        body: [
          "Run the same modest batch of records, ideally ones where you already know the right answer, through each provider you are considering. Then measure what actually happened rather than what was advertised.",
          "Count how many records returned something, how many of those were correct, and how many calls reached the intended person. Those three figures, from your own data, will tell you more than any published percentage."
        ]
      }
    ],
    cta: {
      title: "Try it on your own list",
      body: "98% accuracy, instant results, $0.25 per record and no subscription required. Every plan also includes free skip trace units to start with.",
      label: "Explore Skip Tracing",
      url: "/skip-tracings"
    },
    faqs: [
      {
        question: "How much does skip tracing cost?",
        answer: "$0.25 per record, with no subscription required. Individual plans include 100 free units, the bundle includes 200 and the annual plan includes 300, granted once with no expiry."
      },
      {
        question: "How many phone numbers will I get per record?",
        answer: "We do not promise a fixed count. What returns depends on the person and on the underlying records. Around nine in ten records return contact information you can act on, at 98% accuracy."
      },
      {
        question: "Does LeadCruncher scrub against do-not-call lists?",
        answer: "The dialer can block numbers on a do-not-call list that you upload and maintain. Compliance with calling regulations remains your responsibility as the caller."
      }
    ]
  },
  {
    slug: "assessor-filters-for-motivated-sellers",
    category: "Assessors Data",
    h1: "Assessor Data Filters That Surface Motivated Sellers",
    metaTitle: "Assessor Filters That Surface Motivated Sellers",
    metaDescription:
      "Equity, absentee ownership, length of ownership and deed type separate a targeted list from a county-wide download. Here is how to combine them.",
    excerpt:
      "A county has hundreds of thousands of parcels and you can only call so many people. Filters are how you decide which few hundred are worth the week.",
    lede: "Assessor data is the widest net available, which is exactly the problem. Value comes from the filters you apply, not from the size of the database underneath them.",
    readMinutes: 6,
    updated: "2026-09-14",
    takeaways: [
      "Equity determines whether a seller can actually accept your offer.",
      "Absentee ownership is the single highest-signal filter in most markets.",
      "Length of ownership is a proxy for life stage and for accumulated equity.",
      "Stacking two or three filters beats relying on any one of them."
    ],
    sections: [
      {
        heading: "Start with equity, because it decides what is possible",
        body: [
          "Equity is the gap between what a property is worth and what is owed against it. It sets the boundaries of any deal: an owner with substantial equity has room to negotiate, and an owner with almost none has very little regardless of how motivated they are.",
          "Filtering on equity first means the conversations you start are ones that can actually finish. It is the difference between a motivated seller and a seller who is able to sell."
        ]
      },
      {
        heading: "Absentee ownership",
        body: [
          "An absentee owner is one whose mailing address differs from the property address. They live somewhere else, which usually means the property is a rental, an inheritance, or something acquired and never fully dealt with.",
          "Distance erodes attachment. An out-of-state owner managing a property from three time zones away has a materially different relationship to it than someone living in it, and that shows up in how they respond to an approach."
        ]
      },
      {
        heading: "Length of ownership",
        body: [
          "How long someone has held a property tells you a great deal without telling you anything personal. Long tenure tends to mean accumulated equity, an older owner, and a property that may be overdue for maintenance decisions.",
          "Very short tenure is worth a second look for the opposite reason. Someone who bought recently and is already considering a sale usually has a specific reason, and specific reasons are easier to solve."
        ]
      },
      {
        heading: "Deed type, loan type and property type",
        body: [
          "Deed type describes how the current owner took title, which hints at how the property was acquired. Loan type describes the financing sitting against it. Property type keeps the list inside the asset class you actually work in.",
          "None of these is decisive alone. Together they let you describe a specific situation rather than a broad category, and a specific situation is something you can write a genuinely relevant letter about."
        ],
        bullets: [
          "Deed type: how title was taken.",
          "Loan type: what financing is in place.",
          "Property type: keeps the list inside your asset class.",
          "Location: county, ZIP code, or a shape you draw on the map."
        ]
      },
      {
        heading: "Stack filters, then keep the list small",
        body: [
          "The habit that separates consistent operators from everyone else is stacking. Absentee plus high equity plus long tenure is a far smaller and far better list than any one of those filters alone.",
          "A list you can work properly in a week beats one you will abandon on day three. LeadCruncher covers 160M+ parcel records across 3,000+ counties in all 50 states, refreshed daily, at $60 per month including 10,000 records. The point of that scale is not to download all of it. It is to make sure that whatever narrow slice you want actually exists."
        ]
      }
    ],
    cta: {
      title: "Build your first filtered list",
      body: "160M+ parcel records across 3,000+ counties, refreshed daily, with equity, ownership, tax and deed detail. $60 per month, including pre-foreclosure data at no extra cost.",
      label: "Explore Assessors Data",
      url: "/assessors-data"
    },
    faqs: [
      {
        question: "How many records are included at $60 per month?",
        answer: "10,000 records per month. Additional records beyond the monthly allowance are $0.10 each."
      },
      {
        question: "How often is assessor data refreshed?",
        answer: "Daily, across 3,000+ counties in all 50 states."
      },
      {
        question: "Can I filter by an area I draw myself?",
        answer: "Yes. Alongside county and ZIP code filters you can search by drawing an area directly on the map."
      }
    ]
  }
];

export function findResourceArticle(slug: string | null | undefined): ResourceArticle | undefined {
  return ResourceArticles.find(article => article.slug === slug);
}
